import { apiClient } from '@/shared/services/api/client';
import { assertEnvelopeOk, unwrapEnvelope } from '@/shared/services/api/envelope';
import type { ApiEnvelope } from '@/shared/services/api/envelope';
import type {
  AwardState,
  BidComparison,
  PriceRevision,
  PriceRevisionPayload,
  StartAwardPayload,
} from '../models/bidComparison.types';
import type {
  CodeLabel,
  PageResponse,
  RfqActivity,
  RfqApproval,
  RfqApprovalDecision,
  RfqCreatePayload,
  RfqDeliveryPayload,
  RfqDetail,
  RfqDetailSupplier,
  RfqDetailSuppliers,
  RfqDetailsPayload,
  RfqDocument,
  RfqDraft,
  RfqExportFormat,
  RfqExportScope,
  RfqExtendDeadlinePayload,
  RfqHoldPayload,
  RfqResumePayload,
  RfqFieldError,
  RfqKpis,
  RfqLinePayload,
  RfqLineSchedule,
  RfqListQuery,
  RfqLocationOption,
  RfqProductCategory,
  RfqProductDetail,
  RfqSupplierDetail,
  RfqProductOption,
  RfqRequirementRow,
  RfqRow,
  RfqSubmitResult,
  RfqTemplateCard,
  RfqTemplateDefinition,
  RfqTemplateSort,
  RfqUomOption,
  SourceablePr,
  SourceablePrQuery,
  RfqPurchaseRequestQuery,
  RfqPurchaseRequestRow,
  SupplierDirectoryQuery,
  SupplierDirectoryResult,
  ProductMode,
} from '../models/rfq.types';

/**
 * The only module that knows the RFQ endpoints (`/api/v1/rfqs`, backend `modules/rfq`).
 *
 * **Real API only.** Unlike every other feature, there is no `env.ENABLE_MOCKS` branch and no
 * `__mocks__` fixture here: the product owner asked for this module to run against the platform and
 * nothing else, so a screen with no backend shows the platform's error rather than invented rows. The
 * departure from the repo's single-switch rule is recorded in docs/deviations.md. Tests replace this
 * module with `vi.mock` instead.
 *
 * The `X-Entity-Id` header is not set here: the shell's request interceptor adds it to every call from
 * the entity picker, which is what scopes the list and is required when a draft is created.
 */

const BASE = '/rfqs';
const FALLBACK = 'The BID request could not be completed. Please try again.';

/*
  Background reads opt out of the global 403 -> /unauthorized navigation: a refused list or lookup is the
  screen's news to report in place, not grounds to throw the user out of the shell.
*/
const allowForbidden = { _allowForbidden: true } as const;

const one = (id: number) => `${BASE}/${String(id)}`;

type Params = Record<string, string | number | string[]>;

/**
 * The list's query string. `filter` repeats, one `property:comparison:value` per Filter-dialog row (the
 * masters' convention; the platform splits on the first two colons only, so a value may hold one).
 */
function listParams(query: RfqListQuery, withPaging = true): Params {
  const params: Params = {};
  const search = query.search.trim();
  if (search) params.search = search;
  if (query.statuses.length > 0) params.status = query.statuses;
  if (query.category) params.category = query.category;
  const filters = (query.filters ?? []).filter((entry) => entry.value.trim() !== '');
  if (filters.length > 0) {
    params.filter = filters.map((entry) => `${entry.property}:${entry.comparison}:${entry.value.trim()}`);
  }
  params.sort = `${query.sort.field},${query.sort.direction}`;
  if (withPaging) {
    params.page = query.page;
    params.size = query.size;
  }
  return params;
}

async function getData<T>(url: string, params?: Params): Promise<T> {
  return unwrapEnvelope(
    await apiClient.get<ApiEnvelope<T>>(url, { ...allowForbidden, ...(params ? { params } : {}) }),
    FALLBACK,
  );
}

async function postData<T>(url: string, body?: unknown): Promise<T> {
  return unwrapEnvelope(await apiClient.post<ApiEnvelope<T>>(url, body ?? {}), FALLBACK);
}

async function postMultipart<T>(url: string, form: FormData): Promise<T> {
  return unwrapEnvelope(
    await apiClient.post<ApiEnvelope<T>>(url, form, {
      /* Explicit, or axios would serialise the FormData as JSON under the client's default header. */
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
    FALLBACK,
  );
}

/** Hold's and Resume's shared parts: the trimmed reason and, when chosen, the supporting document. */
function lifecycleForm(payload: RfqHoldPayload): FormData {
  const form = new FormData();
  form.append('reason', payload.reason.trim());
  if (payload.file) form.append('file', payload.file);
  return form;
}

async function putData<T>(url: string, body: unknown): Promise<T> {
  return unwrapEnvelope(await apiClient.put<ApiEnvelope<T>>(url, body), FALLBACK);
}

async function deleteData<T>(url: string): Promise<T> {
  return unwrapEnvelope(await apiClient.delete<ApiEnvelope<T>>(url), FALLBACK);
}

/** A file name from `Content-Disposition`, or the fallback. */
/** "BID-Demobid.pdf": "BID-" and the title, without what a file name cannot hold; "BID.pdf" with no title. */
export function bidPdfFileName(title: string | null | undefined): string {
  const clean = (title ?? '').replace(/[\\/:*?"<>|\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
  return clean ? `BID-${clean}.pdf` : 'BID.pdf';
}

function fileNameFrom(disposition: unknown, fallback: string): string {
  if (typeof disposition !== 'string') return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (star?.[1]) return decodeURIComponent(star[1]);
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1] ?? fallback;
}

export interface DownloadedFile {
  blob: Blob;
  fileName: string;
}

/**
 * The field errors of a refused request, as `GlobalExceptionHandler` writes them:
 * `{ success: false, errors: [{ field, message }] }`.
 *
 * Read here rather than through `normalizeError`, whose `details` expects a map; the submit endpoint's
 * list is what the Review step draws, one line per failure, each keyed `<step>.<field>`.
 */
export function fieldErrorsOf(error: unknown): RfqFieldError[] {
  if (typeof error !== 'object' || error === null) return [];
  const response = (error as { response?: { data?: unknown } }).response;
  const data = response?.data;
  if (typeof data !== 'object' || data === null) return [];
  const errors = (data as { errors?: unknown }).errors;
  if (Array.isArray(errors)) {
    return errors
      .filter(
        (entry): entry is { field?: unknown; message?: unknown } =>
          typeof entry === 'object' && entry !== null,
      )
      .map((entry) => ({
        field: typeof entry.field === 'string' ? entry.field : '',
        message: typeof entry.message === 'string' ? entry.message : '',
      }))
      .filter((entry) => entry.message !== '');
  }
  if (typeof errors === 'object' && errors !== null) {
    return Object.entries(errors as Record<string, unknown>).flatMap(([field, value]) => {
      const messages = Array.isArray(value) ? value : [value];
      return messages
        .filter((message): message is string => typeof message === 'string')
        .map((message) => ({ field, message }));
    });
  }
  return [];
}

export const rfqService = {
  /* -- list --------------------------------------------------------------- */

  list(query: RfqListQuery): Promise<PageResponse<RfqRow>> {
    return getData<PageResponse<RfqRow>>(BASE, listParams(query));
  },

  kpis(): Promise<RfqKpis> {
    return getData<RfqKpis>(`${BASE}/kpis`);
  },

  categories(): Promise<CodeLabel[]> {
    return getData<CodeLabel[]>(`${BASE}/categories`);
  },

  statuses(): Promise<CodeLabel[]> {
    return getData<CodeLabel[]>(`${BASE}/statuses`);
  },

  /**
   * The list as a file. `scope=view` carries the current search, statuses and category; `scope=all`
   * drops them. The columns are the table's.
   */
  async exportList(
    query: RfqListQuery,
    format: RfqExportFormat,
    scope: RfqExportScope,
    columns: readonly string[],
  ): Promise<DownloadedFile> {
    const params: Params = { format, scope, columns: [...columns] };
    if (scope === 'view') Object.assign(params, listParams(query, false));
    else params.sort = `${query.sort.field},${query.sort.direction}`;
    const response = await apiClient.get<Blob>(`${BASE}/export`, { params, responseType: 'blob' });
    return {
      blob: response.data,
      fileName: fileNameFrom(response.headers['content-disposition'], `bids.${format}`),
    };
  },

  duplicate(id: number): Promise<RfqDraft> {
    return postData<RfqDraft>(`${one(id)}/duplicate`);
  },

  /**
   * Ends bidding on a Published BID (it moves to Under Evaluation). With a supporting document -- the BID Overview's
   * Force Close offers one (2026-11-02) -- the call is `multipart/form-data` (`reason`, `file`); without, JSON.
   */
  forceClose(id: number, reason: string, file?: File | null): Promise<RfqDetail> {
    if (file) return postMultipart<RfqDetail>(`${one(id)}/force-close`, lifecycleForm({ reason, file }));
    return postData<RfqDetail>(`${one(id)}/force-close`, { reason });
  },

  async remove(id: number): Promise<void> {
    assertEnvelopeOk(await apiClient.delete<ApiEnvelope<unknown>>(one(id)), FALLBACK);
  },

  /* -- lifecycle ------------------------------------------------------------- */

  /**
   * Any non-terminal RFQ that is not already On Hold, with the required reason and an optional supporting document
   * (2026-11-02): `multipart/form-data` -- `reason`, `file`. From Under Evaluation the hold is internal: no supplier is told.
   */
  hold(id: number, payload: RfqHoldPayload): Promise<RfqDetail> {
    return postMultipart<RfqDetail>(`${one(id)}/hold`, lifecycleForm(payload));
  },

  /**
   * An On Hold RFQ, back to the status it had, with the required reason, an optional supporting document and -- only
   * when bidding reopens -- an optional new deadline (2026-11-02). Parts: `reason`, `newDeadline`, `file`.
   */
  resume(id: number, payload: RfqResumePayload): Promise<RfqDetail> {
    const form = lifecycleForm(payload);
    if (payload.newDeadline) form.append('newDeadline', payload.newDeadline);
    return postMultipart<RfqDetail>(`${one(id)}/resume`, form);
  },

  /** A hold's or a resume's supporting document, from its activity entry (`GET /rfqs/{id}/activity/{activityId}/document`). */
  async downloadActivityDocument(id: number, entry: RfqActivity): Promise<DownloadedFile> {
    const response = await apiClient.get<Blob>(`${one(id)}/activity/${String(entry.id)}/document`, {
      responseType: 'blob',
    });
    return {
      blob: response.data,
      fileName: fileNameFrom(response.headers['content-disposition'], entry.document?.fileName ?? 'document'),
    };
  },

  /** A Published RFQ's deadline, later than the current one and in the future; the reason is optional. */
  extendDeadline(id: number, payload: RfqExtendDeadlinePayload): Promise<RfqDetail> {
    const body: RfqExtendDeadlinePayload = { newDeadline: payload.newDeadline };
    if (payload.reason?.trim()) body.reason = payload.reason.trim();
    return postData<RfqDetail>(`${one(id)}/extend-deadline`, body);
  },

  /** Records an invited supplier's withdrawal (Published or Under Evaluation), with the required reason. */
  withdrawSupplier(id: number, supplierId: number, reason: string): Promise<RfqDetail> {
    return postData<RfqDetail>(`${one(id)}/suppliers/${String(supplierId)}/withdraw`, { reason });
  },

  /** Sends a pending supplier's invitation again, to `email` and any `cc` (the BID Overview, 2026-10-22). */
  resendInvitation(
    id: number,
    supplierId: number,
    body: { email: string; cc: string[] },
  ): Promise<RfqDetailSupplier> {
    return postData<RfqDetailSupplier>(`${one(id)}/suppliers/${String(supplierId)}/resend-invitation`, body);
  },

  /* -- template picker ------------------------------------------------------- */

  /** Active RFQ templates. `category` is the picker's type ('' for All RFQ types). */
  templates(sort: RfqTemplateSort, search = '', category = ''): Promise<PageResponse<RfqTemplateCard>> {
    const params: Params = { sort, page: 0, size: 50 };
    if (search.trim()) params.search = search.trim();
    if (category) params.category = category;
    return getData<PageResponse<RfqTemplateCard>>(`${BASE}/templates`, params);
  },

  /**
   * The template's sections and fields, for its custom fields. The RFQ module serves no read of them, so
   * this is the configuration module's `GET /templates/{id}` -- authorised by the RFQ Template feature, so
   * a role without it gets a refusal the wizard reports in place (and the fields stay unrendered).
   */
  template(templateId: number): Promise<RfqTemplateDefinition> {
    return getData<RfqTemplateDefinition>(`/templates/${String(templateId)}`);
  },

  /** Active Location master rows for the single delivery location's picker (free text stays allowed). */
  async locations(search = ''): Promise<RfqLocationOption[]> {
    const params: Params = { status: 'Active', page: 0, size: 100 };
    if (search.trim()) params.search = search.trim();
    const page = await getData<
      PageResponse<
        RfqLocationOption & {
          cityName?: string | null;
          stateName?: string | null;
          countryName?: string | null;
        }
      >
    >('/locations', params);
    return page.content.map((row) => ({
      id: row.id,
      locationName: row.locationName,
      locationCode: row.locationCode ?? null,
      address: row.address ?? null,
      /* Read from the same answer, for the searchable dropdown's "city, state" line; the request is unchanged. */
      city: row.cityName ?? null,
      state: row.stateName ?? null,
      /* The same answer's country and PIN code, shown auto-filled on Single Location (2026-10-22). */
      country: row.countryName ?? null,
      pinCode: row.pinCode ?? null,
    }));
  },

  /* -- wizard: details ------------------------------------------------------- */

  create(payload: RfqCreatePayload): Promise<RfqDraft> {
    return postData<RfqDraft>(BASE, payload);
  },

  update(id: number, payload: RfqDetailsPayload): Promise<RfqDraft> {
    return putData<RfqDraft>(one(id), payload);
  },

  draft(id: number): Promise<RfqDraft> {
    return getData<RfqDraft>(`${one(id)}/draft`);
  },

  /* -- documents ------------------------------------------------------------- */

  async uploadDocument(id: number, file: File, lineId?: number): Promise<RfqDocument> {
    const form = new FormData();
    form.append('file', file);
    if (lineId !== undefined) form.append('lineId', String(lineId));
    return unwrapEnvelope(
      await apiClient.post<ApiEnvelope<RfqDocument>>(`${one(id)}/documents`, form, {
        /* Explicit, or axios would serialise the FormData as JSON under the client's default header. */
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
      FALLBACK,
    );
  },

  documents(id: number): Promise<RfqDocument[]> {
    return getData<RfqDocument[]>(`${one(id)}/documents`);
  },

  async downloadDocument(id: number, doc: RfqDocument): Promise<DownloadedFile> {
    const response = await apiClient.get<Blob>(`${one(id)}/documents/${String(doc.id)}`, {
      responseType: 'blob',
    });
    return {
      blob: response.data,
      fileName: fileNameFrom(response.headers['content-disposition'], doc.fileName),
    };
  },

  /**
   * Review & Submit's Export PDF: the BID as the review shows it, under its entity's letterhead, drawn by the
   * platform (`GET /rfqs/{id}/pdf`) and named "BID-<title>.pdf" (2026-10-15): the server's name when the response
   * carries it, else the same name made here from the title the screen already shows.
   */
  async downloadPdf(id: number, title?: string | null): Promise<DownloadedFile> {
    const response = await apiClient.get<Blob>(`${one(id)}/pdf`, { responseType: 'blob' });
    return {
      blob: response.data,
      fileName: fileNameFrom(response.headers['content-disposition'], bidPdfFileName(title)),
    };
  },

  async deleteDocument(id: number, docId: number): Promise<void> {
    assertEnvelopeOk(
      await apiClient.delete<ApiEnvelope<unknown>>(`${one(id)}/documents/${String(docId)}`),
      FALLBACK,
    );
  },

  /* -- wizard: products & PR --------------------------------------------------- */

  setProductMode(id: number, mode: ProductMode): Promise<RfqDraft> {
    return putData<RfqDraft>(`${one(id)}/product-mode`, { mode });
  },

  sourceablePrs(query: SourceablePrQuery): Promise<PageResponse<SourceablePr>> {
    const params: Params = { page: query.page, size: query.size };
    if (query.search.trim()) params.search = query.search.trim();
    if (query.category.length > 0) params.category = query.category;
    if (query.product.length > 0) params.product = query.product;
    return getData<PageResponse<SourceablePr>>(`${BASE}/sourceable-purchase-requisitions`, params);
  },

  /**
   * With PR's list (`prlist.jpg`): the Purchase Requests screen's requests a BID may still source from -- Unprocessed,
   * and Partially Processed with products left (2026-10-14)
   * (`GET /purchase-requests?sourceable=true`), of the selected entity and the template's Business Unit,
   * Division and Department, with a line in the BID's category.
   */
  purchaseRequests(query: RfqPurchaseRequestQuery): Promise<PageResponse<RfqPurchaseRequestRow>> {
    const params: Params = { sourceable: 'true', page: query.page, size: query.size };
    if (query.search.trim()) params.search = query.search.trim();
    const ids = (value: number | null) => (value === null ? null : [String(value)]);
    const levels: [string, string[] | null][] = [
      ['legalEntityIds', ids(query.legalEntityId)],
      ['businessUnitIds', ids(query.businessUnitId)],
      ['divisionIds', ids(query.divisionId)],
      ['departmentIds', ids(query.departmentId)],
      ['categoryIds', query.categoryIds.length > 0 ? query.categoryIds.map(String) : null],
    ];
    for (const [name, value] of levels) {
      if (value) params[name] = value;
    }
    return getData<PageResponse<RfqPurchaseRequestRow>>('/purchase-requests', params);
  },

  /** Links `prIds`; with `prLineIds`, each request contributes exactly those of its lines (the products ticked). */
  setPurchaseRequisitions(id: number, prIds: number[], prLineIds?: number[]): Promise<RfqDraft> {
    return putData<RfqDraft>(`${one(id)}/purchase-requisitions`, prLineIds ? { prIds, prLineIds } : { prIds });
  },

  addProducts(id: number, productIds: number[]): Promise<RfqDraft> {
    return postData<RfqDraft>(`${one(id)}/lines`, { productIds });
  },

  addManualLine(id: number, payload: RfqLinePayload): Promise<RfqDraft> {
    return postData<RfqDraft>(`${one(id)}/lines`, payload);
  },

  updateLine(id: number, lineId: number, payload: RfqLinePayload): Promise<RfqDraft> {
    return putData<RfqDraft>(`${one(id)}/lines/${String(lineId)}`, payload);
  },

  /** Every line's quantity and delivery date in one call (With PR and Without PR alike); only those two change. */
  updateLineSchedule(id: number, lines: readonly RfqLineSchedule[]): Promise<RfqDraft> {
    return putData<RfqDraft>(`${one(id)}/lines`, { lines });
  },

  removeLine(id: number, lineId: number): Promise<RfqDraft> {
    return deleteData<RfqDraft>(`${one(id)}/lines/${String(lineId)}`);
  },

  /** Active products by name or code, optionally only those of the given categories (by name). */
  products(search: string, page = 0, size = 20, categories: readonly string[] = []): Promise<PageResponse<RfqProductOption>> {
    const params: Params = { page, size };
    if (search.trim()) params.search = search.trim();
    if (categories.length > 0) params.category = [...categories];
    return getData<PageResponse<RfqProductOption>>(`${BASE}/products`, params);
  },

  /** One supplier in full, whatever its status -- the wizard's View supplier (`GET /rfqs/suppliers/{supplierId}`). */
  supplier(supplierId: number): Promise<RfqSupplierDetail> {
    return getData<RfqSupplierDetail>(`${BASE}/suppliers/${String(supplierId)}`);
  },

  /** One product in full, whatever its status -- the wizard's View product (`GET /rfqs/products/{productId}`). */
  product(productId: number): Promise<RfqProductDetail> {
    return getData<RfqProductDetail>(`${BASE}/products/${String(productId)}`);
  },

  /**
   * The With PR category filter's options: the Active rows of the Category master (`GET /product-categories`,
   * organization module), by name. The platform pages at most 200 rows.
   */
  async productCategories(): Promise<RfqProductCategory[]> {
    const result = await getData<PageResponse<RfqProductCategory>>('/product-categories', {
      status: 'Active',
      page: 0,
      size: 200,
      sort: 'name,asc',
    });
    return result.content.map(({ id, name }) => ({ id, name }));
  },

  uoms(): Promise<RfqUomOption[]> {
    return getData<RfqUomOption[]>(`${BASE}/uoms`);
  },

  /* -- wizard: suppliers ------------------------------------------------------- */

  supplierDirectory(query: SupplierDirectoryQuery): Promise<SupplierDirectoryResult> {
    const params: Params = { page: query.page, size: query.size };
    if (query.search.trim()) params.search = query.search.trim();
    if (query.minRating) params.minRating = query.minRating;
    if (query.rfqId != null) params.rfqId = query.rfqId;
    return getData<SupplierDirectoryResult>(`${BASE}/supplier-directory`, params);
  },

  setSuppliers(id: number, supplierIds: number[]): Promise<RfqDraft> {
    return putData<RfqDraft>(`${one(id)}/suppliers`, { supplierIds });
  },

  /* -- wizard: delivery, declaration, submit -------------------------------------- */

  setDelivery(id: number, payload: RfqDeliveryPayload): Promise<RfqDraft> {
    return putData<RfqDraft>(`${one(id)}/delivery`, payload);
  },

  setDeclaration(id: number, accepted: boolean): Promise<RfqDraft> {
    return putData<RfqDraft>(`${one(id)}/declaration`, { accepted });
  },

  submit(id: number): Promise<RfqSubmitResult> {
    return postData<RfqSubmitResult>(`${one(id)}/submit`);
  },

  /* -- detail ------------------------------------------------------------------ */

  /* `deadlineExtensions` is 0 when the platform leaves it out (a server from before the extension limit). */
  async detail(id: number): Promise<RfqDetail> {
    const record = await getData<RfqDetail>(one(id));
    return { ...record, deadlineExtensions: record.deadlineExtensions ?? 0 };
  },

  requirements(id: number, page: number, size: number): Promise<PageResponse<RfqRequirementRow>> {
    return getData<PageResponse<RfqRequirementRow>>(`${one(id)}/requirements`, { page, size });
  },

  suppliers(
    id: number,
    query: { status: string; search: string; page: number; size: number },
  ): Promise<RfqDetailSuppliers> {
    const params: Params = { page: query.page, size: query.size };
    if (query.status) params.status = query.status;
    if (query.search.trim()) params.search = query.search.trim();
    return getData<RfqDetailSuppliers>(`${one(id)}/suppliers`, params);
  },

  approvals(id: number): Promise<RfqApproval[]> {
    return getData<RfqApproval[]>(`${one(id)}/approvals`);
  },

  act(id: number, approvalId: number, decision: RfqApprovalDecision, remark: string): Promise<RfqDetail> {
    return postData<RfqDetail>(`${one(id)}/approvals/${String(approvalId)}/${decision}`, { remark });
  },

  activity(id: number, page = 0, size = 20): Promise<PageResponse<RfqActivity>> {
    return getData<PageResponse<RfqActivity>>(`${one(id)}/activity`, { page, size });
  },

  /** Bid Comparison (2026-10-28): the submitted quotations side by side, scored, with the four award options. */
  bidComparison(id: number): Promise<BidComparison> {
    return getData<BidComparison>(`${one(id)}/bid-comparison`);
  },

  /** Edit Bid Amount (2026-10-29): revises one quote's amount; answers the comparison as it now stands. */
  async revisePrice(id: number, payload: PriceRevisionPayload): Promise<BidComparison> {
    const form = new FormData();
    form.append('quotationId', String(payload.quotationId));
    form.append('lineId', String(payload.lineId));
    form.append('amount', String(payload.amount));
    form.append('reason', payload.reason);
    if (payload.document) form.append('document', payload.document);
    return unwrapEnvelope(
      await apiClient.post<ApiEnvelope<BidComparison>>(`${one(id)}/bid-comparison/price-revisions`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
      FALLBACK,
    );
  },

  /**
   * Mark non-compliant (2026-10-29), with the reason (required, 2026-11-02): the supplier is shown but never
   * evaluated, picked or revised.
   */
  markNonCompliant(id: number, supplierId: number, reason: string): Promise<BidComparison> {
    return postData<BidComparison>(`${one(id)}/bid-comparison/suppliers/${String(supplierId)}/non-compliant`, {
      reason,
    });
  },

  /** Undo of Mark non-compliant. */
  undoNonCompliant(id: number, supplierId: number): Promise<BidComparison> {
    return deleteData<BidComparison>(`${one(id)}/bid-comparison/suppliers/${String(supplierId)}/non-compliant`);
  },

  /** The award (phase 3, 2026-10-29): the BID's status, whether Start Approval may be used, the award. */
  awardState(id: number): Promise<AwardState> {
    return getData<AwardState>(`${one(id)}/award`);
  },

  /** Start Approval: one supplier's quote per product, sent to the BID's approval workflow. */
  startAward(id: number, payload: StartAwardPayload): Promise<AwardState> {
    return postData<AwardState>(`${one(id)}/award`, payload);
  },

  async downloadRevisionDocument(id: number, revision: PriceRevision): Promise<DownloadedFile> {
    const response = await apiClient.get<Blob>(
      `${one(id)}/bid-comparison/price-revisions/${String(revision.id)}/document`,
      { responseType: 'blob' },
    );
    return {
      blob: response.data,
      fileName: fileNameFrom(response.headers['content-disposition'], revision.documentName ?? 'document'),
    };
  },
};

export type RfqService = typeof rfqService;
